import React, { useEffect, useState, useRef } from 'react';
import { Loader2 } from 'lucide-react';
import { getCampaignGenerationProgress, CampaignGenerationProgress } from '../../services/api';

/**
 * Polling progress overlay for the long-running campaign generation job.
 *
 * Mounts when ``isOpen`` is true and polls the lightweight
 * ``/generation-progress`` endpoint every 1.5s. The actual generation runs
 * out-of-process (aime-scheduler) so the parent's POST returns 202 almost
 * immediately — this modal is what the user actually waits on, not the POST
 * round-trip. Parent unmounts the modal when generation_status hits a
 * terminal value (completed | failed | cancelled).
 */
const CampaignGenerationProgressModal: React.FC<{
    campaignId: string;
    isOpen: boolean;
    onRetry?: () => void | Promise<void>;
    onClose?: () => void;
    onComplete?: () => void;  // Fired once when phase reaches a successful terminal state
}> = ({ campaignId, isOpen, onRetry, onClose, onComplete }) => {
    const [progress, setProgress] = useState<CampaignGenerationProgress | null>(null);
    const intervalRef = useRef<number | null>(null);

    useEffect(() => {
        if (!isOpen) {
            if (intervalRef.current) {
                window.clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            setProgress(null);
            return;
        }

        let cancelled = false;
        const tick = async () => {
            try {
                const p = await getCampaignGenerationProgress(campaignId);
                if (!cancelled) setProgress(p);
            } catch {
                /* polling errors are non-fatal — modal stays up */
            }
        };
        tick();
        intervalRef.current = window.setInterval(tick, 1500);
        return () => {
            cancelled = true;
            if (intervalRef.current) {
                window.clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [isOpen, campaignId]);

    const status = progress?.generation_status;
    const p = progress?.progress;
    const failed = status === 'failed' || p?.phase === 'failed';
    const cancelled = status === 'cancelled' || p?.phase === 'cancelled';
    const succeeded = status === 'completed' || status === 'completed_with_gaps';

    // Race guard: when the modal opens via the card-level Retry button, the
    // POST /generate hasn't necessarily updated the campaign's status to
    // 'queued' before our first poll lands. The first poll then reads the
    // STALE prior terminal status (e.g. 'completed_with_gaps' from the
    // previous run) and onComplete fires immediately — modal flashes off
    // before the new run even started. We require two consecutive terminal
    // observations to fire onComplete; the inverse non-terminal observation
    // resets the counter, so a real "stayed terminal across two polls"
    // (extremely fast genuine completion) still triggers correctly.
    const completedFiredRef = useRef(false);
    const terminalCountRef = useRef(0);
    useEffect(() => {
        if (!progress) return;  // wait for first response
        if (succeeded) {
            terminalCountRef.current += 1;
            if (terminalCountRef.current >= 2 && !completedFiredRef.current && onComplete) {
                completedFiredRef.current = true;
                onComplete();
            }
        } else {
            terminalCountRef.current = 0;  // saw non-terminal — reset
        }
    }, [succeeded, progress, onComplete]);

    if (!isOpen) return null;

    // Phase progression — pure visual cue. Per-variant status replaces this
    // once Phase 2 lands (rollout step 5).
    const PHASE_ORDER = ['queued', 'first_pass', 'variants', 'completed'];
    const phaseIdx = p ? Math.max(0, PHASE_ORDER.indexOf(p.phase)) : 0;
    const pct = Math.min(100, Math.round(((phaseIdx + 1) / PHASE_ORDER.length) * 100));

    // Friendly label per generator step (set by campaign_generator.py payloads).
    const STEP_LABELS: Record<string, string> = {
        bundle: 'Generating campaign brief and asset bundle…',
        core_saved: 'Saving core assets…',
        social: 'Generating social posts from the blog article…',
        ad_copy: 'Generating ad copy variants…',
        lp_upgrade: 'Building landing page records…',
    };
    const message = p?.payload?.message
        || (p?.payload?.step ? STEP_LABELS[p.payload.step] : null)
        || (failed ? 'Please try again.' : cancelled ? 'Cancelled.' : 'Starting…');

    return (
        <div
            style={{
                position: 'fixed', inset: 0, zIndex: 1200,
                background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: 'var(--space-4)',
            }}
        >
            <div
                style={{
                    background: 'var(--panel)', borderRadius: 'var(--radius-lg)',
                    border: '1px solid var(--stroke)', width: '100%', maxWidth: '480px',
                    padding: 'var(--space-5) var(--space-6)',
                    display: 'flex', flexDirection: 'column', gap: 'var(--space-4)',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                    {!failed && !cancelled && !succeeded && <Loader2 size={20} className="spin" style={{ color: 'var(--primary-500)' }} />}
                    <span style={{ fontWeight: 'var(--font-semibold)', color: 'var(--text)', fontSize: 'var(--text-base)' }}>
                        {succeeded ? (status === 'completed_with_gaps' ? 'Generation complete (with gaps)' : 'Generation complete')
                         : failed ? 'Generation failed'
                         : cancelled ? 'Generation cancelled'
                         : 'Generating campaign assets…'}
                    </span>
                </div>
                <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', minHeight: '1.4em' }}>
                    {message}
                </div>
                <div
                    style={{
                        width: '100%', height: '8px',
                        background: 'var(--panel-strong)', borderRadius: '4px',
                        overflow: 'hidden',
                    }}
                >
                    <div
                        style={{
                            width: `${pct}%`, height: '100%',
                            background: failed ? 'var(--danger)' : 'var(--primary-500)',
                            transition: 'width 0.3s ease',
                        }}
                    />
                </div>
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', textAlign: 'right' }}>
                    {p?.phase ? `Phase: ${p.phase.replace('_', ' ')}` : ' '}
                </div>
                {(failed || cancelled) && (onRetry || onClose) && (
                    <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end', marginTop: 'var(--space-2)' }}>
                        {onClose && (
                            <button
                                onClick={onClose}
                                style={{
                                    padding: '8px 16px',
                                    borderRadius: '6px',
                                    border: '1px solid var(--stroke)',
                                    background: 'var(--panel-strong)',
                                    color: 'var(--text)',
                                    cursor: 'pointer',
                                    fontSize: 'var(--text-sm)',
                                }}
                            >
                                Back to wizard
                            </button>
                        )}
                        {onRetry && (
                            <button
                                onClick={onRetry}
                                style={{
                                    padding: '8px 16px',
                                    borderRadius: '9999px',
                                    border: 'none',
                                    background: 'var(--primary-500)',
                                    color: 'white',
                                    cursor: 'pointer',
                                    fontSize: 'var(--text-sm)',
                                    fontWeight: 600,
                                }}
                            >
                                Try again
                            </button>
                        )}
                    </div>
                )}
                {succeeded && onComplete && (
                    <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end', marginTop: 'var(--space-2)' }}>
                        <button
                            onClick={onComplete}
                            style={{
                                padding: '8px 16px',
                                borderRadius: '9999px',
                                border: 'none',
                                background: 'var(--primary-500)',
                                color: 'white',
                                cursor: 'pointer',
                                fontSize: 'var(--text-sm)',
                                fontWeight: 600,
                            }}
                        >
                            View campaign
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CampaignGenerationProgressModal;
